import type { User } from '@supabase/supabase-js';

export type NativeAccountCompletionReason = 'free' | 'purchase' | 'existing_member';
export type NativeAccountCompletionStatus = 'required' | 'deferred';

export interface NativeAccountCompletionState {
  userId: string;
  reason: NativeAccountCompletionReason;
  returnPath: string;
  status: NativeAccountCompletionStatus;
  updatedAt: string;
}

export const NATIVE_ACCOUNT_COMPLETION_STORAGE_KEY =
  'jp_native_account_completion_v1';
export const NATIVE_ACCOUNT_COMPLETION_EVENT =
  'jp:native-account-completion-changed';

const DEFAULT_RETURN_PATH = '/home';

export function isAnonymousUser(user: User | null | undefined): boolean {
  return user?.is_anonymous === true;
}

export function normalizeNativeAccountReturnPath(
  value: string | null | undefined,
  fallback = DEFAULT_RETURN_PATH,
): string {
  const trimmed = value?.trim() ?? '';
  if (!trimmed.startsWith('/') || trimmed.startsWith('//')) return fallback;
  return trimmed;
}

export function getNativeAccountCompletionPath(
  reason: NativeAccountCompletionReason,
  returnPath?: string,
): string {
  const params = new URLSearchParams({
    reason,
    returnTo: normalizeNativeAccountReturnPath(returnPath, DEFAULT_RETURN_PATH),
  });
  return `/complete-account?${params.toString()}`;
}

function emitAccountCompletionChange(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(NATIVE_ACCOUNT_COMPLETION_EVENT));
}

export function readNativeAccountCompletion(
  userId?: string | null,
): NativeAccountCompletionState | null {
  if (typeof window === 'undefined' || !userId) return null;

  try {
    const raw = window.localStorage.getItem(NATIVE_ACCOUNT_COMPLETION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<NativeAccountCompletionState>;
    if (
      parsed.userId !== userId ||
      (parsed.reason !== 'free' && parsed.reason !== 'purchase' && parsed.reason !== 'existing_member') ||
      (parsed.status !== 'required' && parsed.status !== 'deferred') ||
      typeof parsed.returnPath !== 'string' ||
      typeof parsed.updatedAt !== 'string'
    ) {
      return null;
    }
    return {
      userId,
      reason: parsed.reason,
      returnPath: normalizeNativeAccountReturnPath(parsed.returnPath),
      status: parsed.status,
      updatedAt: parsed.updatedAt,
    };
  } catch {
    return null;
  }
}

export function requireNativeAccountCompletion(input: {
  userId: string;
  reason: NativeAccountCompletionReason;
  returnPath?: string;
}): NativeAccountCompletionState {
  const state: NativeAccountCompletionState = {
    userId: input.userId,
    reason: input.reason,
    returnPath: normalizeNativeAccountReturnPath(input.returnPath, DEFAULT_RETURN_PATH),
    status: 'required',
    updatedAt: new Date().toISOString(),
  };

  if (typeof window !== 'undefined') {
    window.localStorage.setItem(
      NATIVE_ACCOUNT_COMPLETION_STORAGE_KEY,
      JSON.stringify(state),
    );
    emitAccountCompletionChange();
  }
  return state;
}

export function deferNativeAccountCompletion(
  userId: string,
): NativeAccountCompletionState | null {
  const current = readNativeAccountCompletion(userId);
  if (!current || (current.reason !== 'purchase' && current.reason !== 'existing_member')) return null;
  const state = {
    ...current,
    status: 'deferred' as const,
    updatedAt: new Date().toISOString(),
  };
  window.localStorage.setItem(
    NATIVE_ACCOUNT_COMPLETION_STORAGE_KEY,
    JSON.stringify(state),
  );
  emitAccountCompletionChange();
  return state;
}

export function clearNativeAccountCompletion(userId?: string | null): void {
  if (typeof window === 'undefined') return;
  const current = userId ? readNativeAccountCompletion(userId) : null;
  if (userId && !current) return;
  window.localStorage.removeItem(NATIVE_ACCOUNT_COMPLETION_STORAGE_KEY);
  emitAccountCompletionChange();
}

export function resolveNativeRootDestination(input: {
  user: User | null;
  onboardingCompleted: boolean;
  accountCompletion: NativeAccountCompletionState | null;
}): string | null {
  if (!input.user) return null;
  if (!isAnonymousUser(input.user)) return '/home';
  if (!input.onboardingCompleted) return '/onboarding';
  if (input.accountCompletion?.status === 'required') {
    return getNativeAccountCompletionPath(
      input.accountCompletion.reason,
      input.accountCompletion.returnPath,
    );
  }
  return '/home';
}
